import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Cable, RadioTower, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { request, requestForm } from "../api";
import { EmptyState, ErrorState, LoadingState, Modal, PageHeader, Panel, StatusPill } from "../components";
import type {
  ConnectorManifest,
  FACePlatform,
  FACePlatformState,
  FACeOperationalMetrics,
  FACeTransportCertificate,
} from "../types";

interface ConnectorDocs {
  manifest: ConnectorManifest;
  operations?: Array<{ id?: string; name?: string; description?: string }>;
  markdown: string;
}

export function IntegrationsPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ConnectorManifest>();
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => request<ConnectorManifest[]>("/connectors"),
  });
  const docs = useQuery({
    queryKey: ["connector-docs", selected?.id],
    queryFn: () => request<ConnectorDocs>(`/connectors/${encodeURIComponent(selected!.id)}/docs`),
    enabled: Boolean(selected),
  });
  const face = useQuery({
    queryKey: ["face-platform"],
    queryFn: () => request<FACePlatformState>("/face/platform"),
  });
  const faceMetrics = useQuery({
    queryKey: ["face-metrics"],
    queryFn: () => request<FACeOperationalMetrics>("/face/metrics"),
    refetchInterval: 60_000,
  });
  const configure = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      request<FACePlatform>("/face/platform", { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["face-platform"] }),
  });
  const upload = useMutation({
    mutationFn: (form: FormData) =>
      requestForm<FACeTransportCertificate>("/face/transport-certificates", form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["face-platform"] }),
  });
  const activate = useMutation({
    mutationFn: (certificateId: string) =>
      request<FACePlatform>(
        `/face/transport-certificates/${encodeURIComponent(certificateId)}/register-and-activate`,
        { method: "POST" },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["face-platform"] }),
  });
  const connectivity = useMutation({
    mutationFn: () => request<FACePlatform>("/face/platform/test", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["face-platform"] }),
  });
  const retire = useMutation({
    mutationFn: (certificateId: string) =>
      request<FACeTransportCertificate>(
        `/face/transport-certificates/${encodeURIComponent(certificateId)}/retire`,
        { method: "POST" },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["face-platform"] }),
  });

  function savePlatform(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    configure.mutate({
      integrator_identifier: form.get("integrator_identifier"),
      name: form.get("name"),
      portal_registered: form.get("portal_registered") === "on",
      services_third_party_enabled: form.get("services_third_party_enabled") === "on",
    });
  }

  function uploadCertificate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    upload.mutate(new FormData(event.currentTarget));
  }

  return (
    <>
      <PageHeader
        eyebrow="Connector registry"
        title="Capabilities Nexus can activate"
        description="The catalog comes directly from the running connector registry, including configuration and credential contracts."
      />
      <Panel title="FACe platform" meta={<StatusPill value={face.data?.platform.state ?? "not configured"} />}>
        <p className="muted">
          One Lueira-owned transport identity for this environment. Tenant Facturae signing
          certificates are deliberately separate.
        </p>
        {face.isLoading && <LoadingState label="Reading FACe platform" />}
        {face.error && <ErrorState error={face.error} />}
        {faceMetrics.data && (
          <dl className="connector-facts">
            <div><dt>Send switch</dt><dd>{faceMetrics.data.send_enabled ? "Enabled" : "Disabled"}</dd></div>
            <div><dt>Authentication</dt><dd>{faceMetrics.data.authentication_failure ? "Attention" : "Healthy"}</dd></div>
            <div><dt>Unknown submissions</dt><dd>{faceMetrics.data.unknown_submissions}</dd></div>
            <div><dt>Poll backlog</dt><dd>{faceMetrics.data.poll_backlog}</dd></div>
            <div><dt>FACe errors (1h)</dt><dd>{faceMetrics.data.remote_errors_last_hour}</dd></div>
            <div>
              <dt>Certificate expiry</dt>
              <dd>
                {faceMetrics.data.certificate_expiry_days == null
                  ? "Not active"
                  : `${faceMetrics.data.certificate_expiry_days} days${
                    faceMetrics.data.expiry_alert_threshold
                      ? ` · alert ${faceMetrics.data.expiry_alert_threshold}d`
                      : ""
                  }`}
              </dd>
            </div>
          </dl>
        )}
        {face.data && (
          <div className="contract-view">
            <dl className="connector-facts">
              <div><dt>Environment</dt><dd>{face.data.platform.environment ?? "current deployment"}</dd></div>
              <div><dt>FACe UUID</dt><dd>{face.data.platform.face_uuid ?? "Pending"}</dd></div>
              <div><dt>Can send</dt><dd>{face.data.platform.can_send ? "Yes" : "No"}</dd></div>
              <div><dt>Can receive</dt><dd>{face.data.platform.can_receive ? "Yes" : "No"}</dd></div>
            </dl>
            <form className="form-stack" onSubmit={savePlatform}>
              <label>Integrator identifier<input name="integrator_identifier" required defaultValue={face.data.platform.integrator_identifier} /></label>
              <label>Platform name<input name="name" required defaultValue={face.data.platform.name ?? "Lueira"} /></label>
              <label><input type="checkbox" name="portal_registered" defaultChecked={Boolean(face.data.platform.portal_registered_at)} /> Portal registration completed</label>
              <label><input type="checkbox" name="services_third_party_enabled" defaultChecked={Boolean(face.data.platform.services_third_party_enabled_at)} /> “Servicios a terceros” accepted</label>
              <button type="submit" className="button button--primary" disabled={configure.isPending}>Save registration state</button>
              {configure.error && <ErrorState error={configure.error} />}
            </form>
            <div>
              <h3>Transport certificates</h3>
              {face.data.certificates.map((certificate) => (
                <div className="inline-note" key={certificate.id}>
                  <ShieldCheck size={17} />
                  <span><strong>{certificate.alias}</strong> · {certificate.status} · expires {new Date(certificate.not_after).toLocaleDateString()}</span>
                  {certificate.id !== face.data.platform.active_certificate_id && (
                    <>
                      {certificate.status !== "retired" && (
                        <button type="button" className="button button--secondary" onClick={() => activate.mutate(certificate.id)}>
                          Register and activate
                        </button>
                      )}
                      {certificate.status === "grace" && (
                        <button type="button" className="button button--secondary" onClick={() => retire.mutate(certificate.id)}>
                          Disassociate and retire
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <form className="form-stack" onSubmit={uploadCertificate}>
              <label>Alias<input name="alias" required /></label>
              <label>PKCS#12<input name="file" type="file" accept=".p12,.pfx,application/x-pkcs12" required /></label>
              <label>Password<input name="password" type="password" autoComplete="new-password" /></label>
              <button type="submit" className="button button--secondary" disabled={upload.isPending}>Stage transport certificate</button>
              <small>Certificate material is encrypted and stored privately; it is never returned by this API.</small>
              {upload.error && <ErrorState error={upload.error} />}
              {activate.error && <ErrorState error={activate.error} />}
              {retire.error && <ErrorState error={retire.error} />}
            </form>
            <button type="button" className="button button--secondary" onClick={() => connectivity.mutate()} disabled={connectivity.isPending || face.data.platform.state !== "ready"}>
              Test FACe authentication
            </button>
            {face.data.platform.last_connectivity_error && <p className="error-state">{face.data.platform.last_connectivity_error}</p>}
          </div>
        )}
      </Panel>
      {connectors.isLoading && <LoadingState />}
      {connectors.error && <ErrorState error={connectors.error} />}
      {connectors.data && connectors.data.length > 0 ? (
        <div className="connector-grid">
          {connectors.data.map((connector) => (
            <Panel key={connector.id} className="connector-card">
              <div className="connector-card__header">
                <span className="connector-card__icon"><Cable size={20} /></span>
                <StatusPill value={connector.status} />
              </div>
              <div>
                <p className="eyebrow">{connector.category}</p>
                <h2>{connector.display_name}</h2>
                <p>{connector.description}</p>
              </div>
              <div className="capability-row">
                {connector.capabilities.map((capability) => <span key={capability}>{capability}</span>)}
              </div>
              <dl className="connector-facts">
                <div><dt>Direction</dt><dd>{connector.directions.join(", ")}</dd></div>
                <div><dt>Authentication</dt><dd>{connector.auth}</dd></div>
                <div><dt>Topics</dt><dd>{connector.subscribed_topics?.length ?? 0}</dd></div>
              </dl>
              <button type="button" className="button button--secondary" onClick={() => setSelected(connector)}>
                Open contract <ArrowUpRight size={15} />
              </button>
            </Panel>
          ))}
        </div>
      ) : !connectors.isLoading && (
        <EmptyState title="No connectors registered" body="Provider manifests will appear after their package is registered with Nexus." />
      )}
      {selected && (
        <Modal title={`${selected.display_name} connector contract`} onClose={() => setSelected(undefined)}>
          {docs.isLoading && <LoadingState />}
          {docs.error && <ErrorState error={docs.error} />}
          {docs.data && (
            <div className="contract-view">
              <div className="inline-note"><RadioTower size={17} /> Generated from the running manifest.</div>
              <pre>{docs.data.markdown}</pre>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
